import { useEffect, useState, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

import { Button, IconButton } from '@client:common/button';
import { Icon } from '@client:common/icon';
import { Dialog } from '@client:common/overlay/dialog';
import { FormControl, ShortTextFormField } from '@client:common/form';
import { Notification } from '@client:common/overlay/notification';
import { Ripple } from '@client:common/effects/ripple';

import { useSortableData } from './hooks/useSortableData';
import Pagination from './views/pagination/Pagination';
import './SessionHistory.light.scss';
import './SessionHistory.dark.scss';

interface AllSessions {
  created: string;
  updated: string;
  status: string;
  bestMatch?: string;
  name: string;
  sessionULID: string;
  lastStep?: string;
}

interface Session {
  ULID: string;
  created: string;
  updatedAt: string;
  createdByUserID: string;
  createdByUsername: string;
  entityType: string;
  session: any;
}

export const SessionHistory = (props: any) => {
  const { setLoadSession } = props;
  const [allSessions, setAllSessions] = useState<AllSessions[]>([]);
  const navigate = useNavigate();
  const [currentActiveSession, setCurrentActiveSession] =
    useState<AllSessions | null>(null);
  const [currentActiveEl, setCurrentActiveEl] = useState<HTMLElement | null>(null);
  const sessionNameRef = useRef(new FormControl(currentActiveSession?.name));

  useEffect(() => {
    sessionNameRef.current.setValue(currentActiveSession?.name);
  }, [currentActiveSession]);

  const getSessions = async () => {
    const transformedSessions: AllSessions[] = [];

    // Get the access token using the new API
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';

    const resp = await fetch('/api/session/listUserSessions', {
      method: 'POST',
      body: JSON.stringify({
        accessToken: token
      })
    });

    const sessions = await resp.json();

    const dateTimeRefactor = (dateTime: string) => {
      const date = dateTime.split('T')[0];
      const time: any[] = dateTime.split('T')[1].split(':');
      let formattedTime = '';

      if (parseInt(time[0], 10) === 0) {
        formattedTime = `12:${time[1]} AM`;
      } else if (parseInt(time[0], 10) < 12) {
        formattedTime = `${parseInt(time[0], 10)}:${time[1]} AM`;
      } else if (parseInt(time[0], 10) === 12) {
        formattedTime = `12:${time[1]} PM`;
      } else if (parseInt(time[0], 10) > 12) {
        formattedTime = `${parseInt(time[0], 10) - 12}:${time[1]} PM`;
      }

      return `${date} ${formattedTime}`;
    };

    sessions.forEach((sessionRecord: Record<string, Session>) => {
      if (sessionRecord[1].session) {
        transformedSessions.push({
          created: dateTimeRefactor(sessionRecord[1].created),
          updated: dateTimeRefactor(sessionRecord[1].created),
          status: sessionRecord[1].session.complete ? 'Completed' : 'In-Progress',
          bestMatch: sessionRecord[1].session.complete
            ? sessionRecord[1].session.state.rankings[0].model.name
            : 'N/A',
          lastStep: sessionRecord[1].session.lastPageLabel,
          name: sessionRecord[1].session.name,
          sessionULID: sessionRecord[1].ULID
        });
      }
    });

    setAllSessions(transformedSessions);
    return sessions;
  };

  const [currentSessions, setCurrentSessions] = useState([]);

  useEffect(() => {
    getSessions();
    // setLoadSession(null);
  }, []);

  const onPageChanged = (data: any) => {
    const { currentPage, pageLimit } = data;
    const offset = (currentPage - 1) * pageLimit;
    const activeSessions = allSessions.slice(offset, offset + pageLimit);
    setCurrentSessions(activeSessions);
  };

  const totalSessions = allSessions.length;

  const showSessionOptions = (
    e: React.MouseEvent<HTMLElement>,
    session: any
  ) => {
    const newElement = e.currentTarget.children[5] as HTMLElement;
    if (currentActiveEl) {
      currentActiveEl.style.visibility = 'hidden';
    }
    newElement.style.visibility = 'visible';
    setCurrentActiveEl(newElement);
    setCurrentActiveSession(session);
  };

  const getSession = async () => {
    // Get the access token using the new API
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';

    return await fetch('/api/session/getSession', {
      method: 'POST',
      body: JSON.stringify({
        accessToken: token,
        sessionULID: currentActiveSession?.sessionULID
      })
    })
      .then((resp) => resp.json())
      .then((data) => data);
  };

  const deleteSession = async () => {
    // Get the access token using the new API
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';

    const currentSession = await getSession();

    await fetch('/api/session/deleteSession', {
      method: 'POST',
      body: JSON.stringify({
        accessToken: token,
        sessionULID: currentActiveSession?.sessionULID
      })
    }).then((resp) => resp.json());

    Notification.open(`Session "${currentSession.session.name}" deleted.`);
    await getSessions();
  };

  const updateSessionName = async (updatedName: string) => {
    // Get the access token using the new API
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';

    const currentSession = await getSession();
    currentSession.session.name = updatedName;

    await fetch('/api/session/updateSession', {
      method: 'POST',
      body: JSON.stringify({
        accessToken: token,
        sessionULID: currentSession.ULID,
        session: currentSession.session
      })
    })
      .then((resp) => resp.json())
      .then((data) => data);

    Notification.open(`Session "${updatedName}" updated.`);
    await getSessions();
  };

  const displaySessionDialog = async () => {
    const dialogRef = Dialog.create(
      <>
        <IconButton
          className='session-dialog__close'
          style='basic'
          color='primary'
          onClick={() => {
            dialogRef.close();
          }}
        >
          <Icon icon='close' />
        </IconButton>
        <>
          <h1 className='modal-heading'>Edit session name</h1>
          <ShortTextFormField
            label='Session name (optional)'
            formControl={sessionNameRef.current}
          />
          <div className='confirm-name-container'>
            <Button
              style='raised'
              color='primary'
              onClick={() => {
                dialogRef.close();
                updateSessionName(sessionNameRef.current.getValue());
              }}
            >
              CONFIRM NAME
            </Button>
          </div>
        </>
      </>
    )
      .addClassName('session-name-dialog')
      .open();
  };

  const handleResumeSession = () => {
    handleGetSession().then(() => {
      navigate('/pages/selection-wizard');
    });
  };

  const handleStartNewSession = () => {
    handleGetSession().then(() => {
      navigate('/pages/selection-wizard');
    });
  };

  const handleGetSession = async () => {
    const sessionToLoad = await getSession();
    setLoadSession(sessionToLoad);
  };

  const { items, requestSort, sortConfig } = useSortableData(currentSessions);

  const getClassNamesFor = (name: any) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <div className='session-history-container'>
      <h2>Session History</h2>
      {allSessions.length > 0 ? (
        <div className='session-table'>
          <table>
            <tbody>
              <tr>
                <th>
                  <div
                    className='table-header'
                    onClick={() => requestSort('created')}
                  >
                    <h4 className={getClassNamesFor('created')}>
                      Created Time
                    </h4>
                    <Icon
                      className='icon-sort'
                      icon='sort' />
                  </div>
                </th>
                <th>
                  <div
                    className='table-header'
                    onClick={() => requestSort('updated')}
                  >
                    <h4 className={getClassNamesFor('updated')}>
                      Last Modified
                    </h4>
                    <Icon
                      className='icon-sort'
                      icon='sort' />
                  </div>
                </th>
                <th>
                  <div
                    className='table-header'
                    onClick={() => requestSort('status')}
                  >
                    <h4 className={getClassNamesFor('status')}>Status</h4>
                    <Icon
                      className='icon-sort'
                      icon='sort' />
                  </div>
                </th>
                <th>
                  <div
                    className='table-header'
                    onClick={() => requestSort('bestMatch')}
                  >
                    <h4 className={getClassNamesFor('bestMatch')}>
                      Best Match
                    </h4>
                    <Icon
                      className='icon-sort'
                      icon='sort' />
                  </div>
                </th>
                <th>
                  <div
                    className='table-header'
                    onClick={() => requestSort('name')}
                  >
                    <h4 className={getClassNamesFor('name')}>Session Name</h4>
                    <Icon
                      className='icon-sort'
                      icon='sort' />
                  </div>
                </th>
                <th></th>
              </tr>
              {items.map((session) => {
                return (
                  <tr
                    className='session'
                    key={session.sessionULID}
                    onClick={(e) => showSessionOptions(e, session)}
                  >
                    <td>
                      <p>{session.created}</p>
                    </td>
                    <td>
                      <p>{session.updated}</p>
                    </td>
                    <td>
                      <p>{session.status}</p>
                    </td>
                    <td>
                      <p>{session.bestMatch}</p>
                    </td>
                    <td>
                      <p>{session.name}</p>
                    </td>
                    <td style={{ visibility: 'hidden' }}>
                      {session.status === 'Completed' ? (
                        <Button
                          style='basic'
                          color='primary'
                          className='tooltip'
                          onClick={handleStartNewSession}
                        >
                          DUPLICATE
                        </Button>
                      ) : (
                        <Button
                          style='basic'
                          color='primary'
                          className='tooltip'
                          onClick={handleResumeSession}
                        >
                          RESUME
                          <Ripple />
                          <span className='tooltiptext'>
                            Last Step: {session.lastStep}
                          </span>
                        </Button>
                      )}
                      <Button
                        style='basic'
                        color='primary'
                        onClick={displaySessionDialog}
                      >
                        EDIT NAME
                        <Ripple />
                      </Button>
                      <Button
                        style='basic'
                        color='primary'
                        onClick={deleteSession}
                      >
                        DELETE
                        <Ripple />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className='no-session-container'>
          <p>Your saved progress or completed sessions will be listed here.</p>
        </div>
      )}
      <Pagination
        totalRecords={totalSessions}
        pageLimit={10}
        pageNeighbors={1}
        onPageChanged={onPageChanged}
      />
    </div>
  );
};
